/* External Deps */
import { FC, ReactElement, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { tokens } from "@/tokens";

/* Internal Deps */
import { useExcelUpload } from "@/hooks";
import ExcelUpload from "../excel-upload";
import ResponseDisplay from "../response-display";
import CertificateVerification from "../certificate-verification";

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  title?: string;
  subtitle?: string;
}

const steps = ["Select File & Type", "Upload & Process", "Verify & Edit"];

const UploadModal: FC<UploadModalProps> = ({
  open,
  onClose,
  onUploadSuccess,
  title = "Upload Certificate",
  subtitle = "Upload your Excel file to extract certificate data using AI processing.",
}): ReactElement => {
  const [activeStep, setActiveStep] = useState(0);
  const [processedData, setProcessedData] = useState<any>(null);
  const [verifiedData, setVerifiedData] = useState<CertificateExtractedData | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [formType, setFormType] = useState<string>("USMCA");
  const [skipVerification, setSkipVerification] = useState(false);
  const [certificateId, setCertificateId] = useState<string | null>(null);

  const { uploadAndProcessExcel, isUploading, canUpload, clientId } = useExcelUpload();

  const handleFileAndTypeSelection = async (file: File) => {
    setUploadedFile(file);
  };

  const handleProceedToUpload = async () => {
    if (!uploadedFile || !canUpload()) {
      return;
    }

    setActiveStep(1);

    try {
      const result = await uploadAndProcessExcel(uploadedFile, formType);
      setProcessedData(result);
      
      if (result.success && result.data) {
        // Move to verification step
        setActiveStep(2);
        setVerifiedData(result.data);
        // Capture certificate ID for saving changes
        setCertificateId(result.certificateId || null);
      } else {
        // Skip verification if no data - show error and stay on step 1
        setSkipVerification(true);
        // Stay on current step to show error
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setProcessedData({
        success: false,
        message: "Failed to process Excel file. Please try again.",
      });
      setSkipVerification(true);
      // Stay on current step to show error
    }
  };

  const handleVerificationComplete = (data: CertificateExtractedData) => {
    setVerifiedData(data);
    setProcessedData({
      ...processedData,
      data: data,
    });
    
    // Call success callback if provided
    if (onUploadSuccess) {
      onUploadSuccess();
    }
    
    // Close modal after successful verification
    handleClose();
  };

  const handleClose = () => {
    // Reset state when closing
    setActiveStep(0);
    setProcessedData(null);
    setVerifiedData(null);
    setUploadedFile(null);
    setFormType("USMCA");
    setSkipVerification(false);
    setCertificateId(null);
    onClose();
  };

  const handleUploadAnother = () => {
    setActiveStep(0);
    setProcessedData(null);
    setVerifiedData(null);
    setUploadedFile(null);
    setSkipVerification(false);
    setCertificateId(null);
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            {/* Form Type Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Certificate Form Type</FormLabel>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => setFormType(e.target.value)}
              >
                <FormControlLabel value="USMCA" control={<Radio />} label="USMCA/CUSMA/T-MEC" />
                <FormControlLabel value="OTHER" control={<Radio />} label="Other" />
              </RadioGroup>
            </FormControl>

            {formType === "USMCA" && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Items with origin from Canada or Mexico will be automatically marked as included for USMCA.
              </Alert>
            )}

            <ExcelUpload 
              onFileUpload={handleFileAndTypeSelection} 
              isUploading={isUploading}
              disabled={!canUpload()}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            minHeight: 200,
            textAlign: "center"
          }}>
            {isUploading ? (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Processing {uploadedFile?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  Please wait while we extract the data from your Excel file...
                </Typography>
              </>
            ) : (
              processedData && !processedData.success && (
                <Box sx={{ width: '100%' }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Processing Failed
                    </Typography>
                    <Typography variant="body2">
                      {processedData.message || 'Failed to process Excel file. Please try again.'}
                    </Typography>
                  </Alert>
                  <ResponseDisplay response={processedData} />
                </Box>
              )
            )}
          </Box>
        );

      case 2:
        return verifiedData && !skipVerification ? (
          <CertificateVerification
            data={verifiedData}
            formType={formType}
            onSave={handleVerificationComplete}
            certificateId={certificateId || undefined}
          />
        ) : null;


      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "600px",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
            {subtitle}
          </Typography>
        </Box>
        
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Client Selection Warning */}
        {!canUpload() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please select a client to upload certificates. The certificate will be associated with the selected client.
          </Alert>
        )}

        {/* Current Client Info */}
        {canUpload() && clientId && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Certificate will be uploaded for the currently selected client.
          </Alert>
        )}

        {/* Progress Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={activeStep > index || (index === 2 && skipVerification)}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        {getStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {activeStep === 0 && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedToUpload}
              variant="contained"
              color="primary"
              disabled={!uploadedFile || !canUpload()}
              sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, boxShadow: "none" }}
            >
              Next: Upload & Process
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isUploading}
              sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
            >
              Cancel
            </Button>
            {processedData && !processedData.success && (
              <Button
                onClick={handleUploadAnother}
                variant="contained"
                color="primary"
                sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, bgcolor: tokens.brand.primary, "&:hover": { bgcolor: tokens.brand.primaryHover }, boxShadow: "none" }}
              >
                Try Again
              </Button>
            )}
          </>
        )}

        {activeStep === 2 && (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, px: 2, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
            >
              Cancel
            </Button>
            <Typography variant="body2" sx={{ color: tokens.text.secondary, flexGrow: 1, textAlign: 'center' }}>
              Save changes to complete the upload
            </Typography>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadModal;